import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { SuperAdminSidebar } from '../../components/Sidebar';
import api from '../../utils/api';

const PLAN_LABELS = { free: 'Kostenlos', standard: 'Standard', premium: 'Premium' };

function CreateInvoiceModal({ onClose, onCreated }) {
  const [mosques, setMosques] = useState([]);
  const [prices, setPrices] = useState({ standard_price_chf: 29, premium_price_chf: 59 });
  const [form, setForm] = useState({
    mosqueId: '', plan: 'standard', periodMonths: 1,
    periodStart: new Date().toISOString().slice(0, 10),
    customAmount: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/superadmin/mosques?limit=200').then(r => {
      setMosques(r.data.mosques || []);
    });
    api.get('/superadmin/settings').then(r => {
      const s = r.data.settings || {};
      setPrices({
        standard_price_chf: parseFloat(s.standard_price_chf) || 29,
        premium_price_chf:  parseFloat(s.premium_price_chf)  || 59,
      });
    });
  }, []);

  const set = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const planPrices = { free: 0, standard: prices.standard_price_chf, premium: prices.premium_price_chf };
  const calculatedAmount = form.customAmount
    ? parseFloat(form.customAmount)
    : (planPrices[form.plan] || 0) * parseInt(form.periodMonths || 1);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.post('/superadmin/invoices', {
        mosqueId: form.mosqueId,
        plan: form.plan,
        periodMonths: parseInt(form.periodMonths),
        periodStart: form.periodStart,
        amount: calculatedAmount,
      });
      onCreated(r.data.invoice);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 32, width: 480, maxHeight: '90vh', overflowY: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 20, marginBottom: 24 }}>Rechnung erstellen</h2>
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Moschee *</label>
            <select className="form-select" name="mosqueId" value={form.mosqueId} onChange={set} required>
              <option value="">Bitte wählen...</option>
              {mosques.map(m => (
                <option key={m.id} value={m.id}>{m.name} — {m.city}</option>
              ))}
            </select>
          </div>
          <div className="grid-2" style={{ gap: 16 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Plan *</label>
              <select className="form-select" name="plan" value={form.plan} onChange={set}>
                <option value="free">Kostenlos</option>
                <option value="standard">Standard (CHF {prices.standard_price_chf}/Mt.)</option>
                <option value="premium">Premium (CHF {prices.premium_price_chf}/Mt.)</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Laufzeit (Monate) *</label>
              <select className="form-select" name="periodMonths" value={form.periodMonths} onChange={set}>
                {[1,2,3,6,12].map(n => <option key={n} value={n}>{n} Monat{n > 1 ? 'e' : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Abrechnungsstart *</label>
            <input className="form-input" type="date" name="periodStart" value={form.periodStart} onChange={set} required />
          </div>
          <div className="form-group">
            <label className="form-label">
              Betrag (CHF)&nbsp;
              <span style={{ color: 'var(--text3)', fontWeight: 400 }}>— leer lassen für automatisch</span>
            </label>
            <input
              className="form-input" type="number" name="customAmount"
              value={form.customAmount} onChange={set}
              step="0.01" min="0"
              placeholder={`CHF ${calculatedAmount.toFixed(2)} (automatisch)`}
            />
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 4 }}>Rechnungsbetrag</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>CHF {calculatedAmount.toFixed(2)}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {PLAN_LABELS[form.plan]} × {form.periodMonths} Monat{parseInt(form.periodMonths) > 1 ? 'e' : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Erstellen...' : 'Rechnung erstellen'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={onClose}>Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = () => {
    setLoading(true);
    api.get('/superadmin/invoices').then(r => setInvoices(r.data.invoices || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/superadmin/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setMsg('Fehler: PDF konnte nicht geladen werden');
    }
  };

  const markPaid = async (inv) => {
    const paymentDate = window.prompt('Zahlungsdatum (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
    if (!paymentDate) return;
    try {
      await api.patch(`/superadmin/invoices/${inv.id}/mark-paid`, {
        paymentDate,
        paymentMethod: 'bank_transfer',
      });
      setMsg(`Rechnung ${inv.invoice_number} als bezahlt markiert`);
      load();
    } catch (err) {
      setMsg('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const markUnpaid = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} als ausstehend markieren?`)) return;
    try {
      await api.patch(`/superadmin/invoices/${inv.id}/mark-unpaid`);
      setMsg(`Rechnung ${inv.invoice_number} als ausstehend markiert`);
      load();
    } catch (err) {
      setMsg('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich löschen?`)) return;
    try {
      await api.delete(`/superadmin/invoices/${inv.id}`);
      setMsg(`Rechnung ${inv.invoice_number} gelöscht`);
      load();
    } catch (err) {
      setMsg('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const filtered = invoices.filter(inv => filterStatus === 'all' || inv.status === filterStatus);

  const totals = {
    total: invoices.reduce((s, i) => s + parseFloat(i.amount || 0), 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.amount || 0), 0),
    pending: invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + parseFloat(i.amount || 0), 0),
  };

  return (
    <div style={{ display: 'flex' }}>
      <SuperAdminSidebar />
      <main className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28 }}>Rechnungen</h1>
            <p style={{ color: 'var(--text3)', fontSize: 14 }}>Rechnungsverwaltung für alle Moscheen</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Rechnung erstellen</button>
        </div>

        {msg && <div className="alert alert-success" style={{ marginBottom: 20 }}>{msg}</div>}

        {/* Summary Stats */}
        <div className="grid-3" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-label">Gesamt</div>
            <div className="stat-value">CHF {totals.total.toFixed(2)}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{invoices.length} Rechnungen</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Bezahlt</div>
            <div className="stat-value" style={{ color: 'var(--primary)' }}>CHF {totals.paid.toFixed(2)}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{invoices.filter(i => i.status === 'paid').length} Rechnungen</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Ausstehend</div>
            <div className="stat-value" style={{ color: '#B8650C' }}>CHF {totals.pending.toFixed(2)}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{invoices.filter(i => i.status !== 'paid').length} Rechnungen</div>
          </div>
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['all', 'Alle'], ['pending', 'Ausstehend'], ['paid', 'Bezahlt']].map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${filterStatus === val ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus(val)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="spinner-wrap"><div className="spinner"/></div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <FileText size={40} strokeWidth={1.5} color="var(--text3)" />
              </div>
              <div style={{ marginBottom: 16 }}>Keine Rechnungen vorhanden</div>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Erste Rechnung erstellen</button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Rechnungs-Nr.</th>
                    <th>Moschee</th>
                    <th>Plan</th>
                    <th>Betrag</th>
                    <th>Status</th>
                    <th>Datum</th>
                    <th>Fällig bis</th>
                    <th style={{ width: 130 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{inv.invoice_number}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{inv.mosque_name}</div>
                        {inv.mosque_city && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{inv.mosque_city}</div>}
                      </td>
                      <td><span className="badge badge-blue">{PLAN_LABELS[inv.plan] || inv.plan}</span></td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        CHF {parseFloat(inv.amount || 0).toFixed(2)}
                      </td>
                      <td>
                        <span className={`badge ${inv.status === 'paid' ? 'badge-green' : 'badge-orange'}`}>
                          {inv.status === 'paid' ? 'Bezahlt' : 'Ausstehend'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text3)', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {new Date(inv.created_at).toLocaleDateString('de-CH')}
                      </td>
                      <td style={{ color: inv.status !== 'paid' ? '#B8650C' : 'var(--text3)', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString('de-CH') : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {inv.status !== 'paid' ? (
                            <button className="btn btn-sm btn-secondary" onClick={() => markPaid(inv)}>
                              Bezahlt
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-ghost" onClick={() => markUnpaid(inv)}>
                              Ausstehend
                            </button>
                          )}
                          {inv.pdf_path && (
                            <button className="btn btn-sm btn-ghost" onClick={() => downloadPdf(inv)}>
                              PDF
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-ghost"
                            style={{ color: 'var(--error)' }}
                            onClick={() => deleteInvoice(inv)}
                          >
                            Loschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showCreate && (
          <CreateInvoiceModal
            onClose={() => setShowCreate(false)}
            onCreated={(inv) => {
              setShowCreate(false);
              setMsg(`Rechnung ${inv.invoice_number} wurde erfolgreich erstellt`);
              load();
            }}
          />
        )}
      </main>
    </div>
  );
}
